// @ts-strict-ignore
import React, { useCallback, useContext, useMemo } from 'react';
import {
  Box,
  Button, ButtonSize, ButtonVariant, IconName,
} from '@usealloy/component-library';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectNodesById,
  selectUnsetTagNodeIds,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { setFocusedNodeId } from 'flow/store/redux/slice';
import { usePanReactFlowToNode } from 'flow/editor/components/WorkflowCanvas/hooks';

import type { Action } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import {
  actionHasDisplayName,
  getActionName,
  getActionTypeText,
  prunedActionHasRules,
} from 'flow/service/actions';
import { ContainerType } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/types';
import OutputAttributeExpressionTab from 'flow/editor/components/NodeEditorDrawer/OutputAttributeExpressionTab';
import { parseBooleanString } from 'flow/service/util';
import { StyledPre, StyledAction } from 'flow/editor/styles';
import RuleBuilderContainer from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/RuleBuilderContainer';

import { doesActionSupportTooltip } from '../helpers';
import {
  OnHoverOverlay,
  SnippetTooltipHelperText,
  StyledTooltipWrapper,
  TruncatedSnippetDescription,
} from './styles';
import {
  exitLinkedChildActionThunk,
} from 'flow/store/redux/thunks';
import { UserContext } from '~/dashboard/context';
import { getIsEditable } from 'flow/service/general';
import { useEditorAccess } from 'flow/editor/context';
import OutcomeContributingBadge from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/RuleExplainability/OutcomeContributingBadge';


export type NodeTooltipProps = {
  action: Action;
  canClickToAction?: boolean;
};
export default function NodeTooltip({
  action,
  canClickToAction,
}: NodeTooltipProps) {
  const { featureFlags } = useContext(UserContext);
  const dispatch = useAppDispatch();
  const panViewportToNode = usePanReactFlowToNode();
  const graphNodesById = useAppSelector(selectNodesById);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const unsetTagNodeIds = useAppSelector(selectUnsetTagNodeIds);
  const isTagNodeSet = useMemo(() => !(unsetTagNodeIds.includes(action.publicId)),
    [action.publicId, unsetTagNodeIds]);

  const actionTooltipWithRules = useMemo(() => {
    const thenVerb = (
      action.type === ActionType.MATRIX_MODEL
      || action.type === ActionType.SERVICE
    ) ? 'run' : 'set';

    return (
      <Box
        gap={4}
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        {!inEditMode && isTagNodeSet && featureFlags?.ruleExplainabilityPhase >= 4 && (
          <OutcomeContributingBadge />
        )}
        <RuleBuilderContainer
          sourceAction={action}
          containerType={ContainerType.TOOLTIP}
        />
        <StyledPre containerType={ContainerType.TOOLTIP}>
          Then {thenVerb}{' '}
          <StyledAction action={action}>
            {actionHasDisplayName(action) && !canClickToAction
              ? `${getActionName(action)} (${action.service.name})`
              : getActionName(action)}
          </StyledAction>
        </StyledPre>
      </Box>
    );
  }, [action, canClickToAction, inEditMode, isTagNodeSet, featureFlags]);

  const buildTooltipContent = useCallback(
    (
      actionName: string,
      postActionContent?: React.ReactNode,
      prefixContent?: React.ReactNode,
    ) => (
      <StyledPre containerType={ContainerType.TOOLTIP} style={{ overflow: 'visible' }}>
        {prefixContent}
        <StyledAction action={action}>
          {actionName}
        </StyledAction>
        {postActionContent}
      </StyledPre>
    ), [action]);

  const tooltipContent = useMemo(() => {
    switch (action.type) {
      case ActionType.MATRIX_MODEL:
      case ActionType.SERVICE: {
        if (prunedActionHasRules(action, workflowData.thresholds)) return actionTooltipWithRules;
        const actionName = actionHasDisplayName(action) && !canClickToAction
          ? `${getActionName(action)} (${action.service.name})`
          : getActionName(action);
        return buildTooltipContent(actionName, ' always runs');
      }
      case ActionType.INPUT: {
        const actionTitle = `${getActionName(action)}: ${action.input.dataType}`;
        const actionContent = parseBooleanString(action.input.useJq)
          ? (<><br />{`JQ: ${action.input.jqFunction}`}</>)
          : null;

        return buildTooltipContent(actionTitle, actionContent);
      }
      case ActionType.TAG:
        return actionTooltipWithRules;
      case ActionType.OUTPUT: {
        const actionTitle = `${getActionName(action)}: ${action.output.dataType}`;
        const actionContent = (<OutputAttributeExpressionTab action={action} editable={false} />);
        return buildTooltipContent(actionTitle, actionContent);
      }
      case ActionType.SNIPPET: {
        const prefixContent = (
          <SnippetTooltipHelperText>
            Double click to expand
          </SnippetTooltipHelperText>
        );
        const description = workflowProps?.snippets[action.snippet?.token]?.description;
        const actionContent = description
          ? (
            <>
              <br />
              <TruncatedSnippetDescription>
                {description}
              </TruncatedSnippetDescription>
            </>
          )
          : null;
        return buildTooltipContent(
          getActionName(action),
          actionContent,
          prefixContent,
        );
      }
      default:
        return buildTooltipContent(getActionName(action));
    }
  }, [
    action, actionTooltipWithRules, buildTooltipContent,
    canClickToAction, workflowProps, workflowData,
  ]);

  if (!doesActionSupportTooltip(action)) {
    return null;
  }

  if (canClickToAction) {
    const onHoverTooltipClick = () => {
      dispatch(exitLinkedChildActionThunk({}));
      dispatch(setFocusedNodeId(action.publicId));
      const targetNode = graphNodesById[action.publicId];
      if (targetNode) {
        panViewportToNode(targetNode);
      }
    };

    return (
      <StyledTooltipWrapper>
        {tooltipContent}
        <OnHoverOverlay>
          <Button
            variant={ButtonVariant.outline}
            icon={IconName.Undo}
            size={ButtonSize.small}
            onMouseDown={onHoverTooltipClick}
          >
            {`Go to ${getActionTypeText(action)}`}
          </Button>
        </OnHoverOverlay>
      </StyledTooltipWrapper>
    );
  }

  return tooltipContent;
}
